import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Container } from "../../Components/Common/Tailwind";

import _default from "flatpickr/dist/l10n/ko.js";
import Flatpickr from "react-flatpickr";
import { Oval } from "react-loader-spinner";
import { Clock, Calendar as CalendarIcon } from "lucide-react";

import { API } from "../../api";
import campIcon from "../../assets/images/camp_icon.png";
import { SITE_ID, SUBDOMAIN } from "../../assets/meta/meta";
import moment from "moment";
import { getNextFriday, getNextSaturday } from "../../common/utils/dateUtil";
import { numberWithCommas } from "../../common/utils/format_util";
import {
    decorateFlatpickrDay,
    formatDisplayHolidayRowText,
    getDisplayHolidayMonthParams,
    getDisplayHolidayRows,
    getDisplayHolidaySourceLabel,
    isSiteDisplayHolidayDate,
    normalizeDisplayHolidayResponse,
    SITE_DISPLAY_HOLIDAY_CLOSED_MESSAGE,
} from "../../common/siteDisplayHolidays.mjs";

const LodgingPackageCalendar = ({
    membership_product_id = "",
    package_api = "/package/lodge-package/",
    skip_to_order = false,
    use_amazingpass = false
}) => {
    const navigate = useNavigate();

    // const [calendarOpen, setCalendarOpen] = useState(true);
    const [selectedStartDate, setSelectedStartDate] = useState("");
    const [selectedEndDate, setSelectedEndDate] = useState("");

    const [packages, setPackages] = useState([]);
    const [toggleId, setToggleId] = useState(-1);
    const [selectedSpotId, setSelectedSpotId] = useState([]);
    const [displayHolidayData, setDisplayHolidayData] = useState(null);
    const [siteHolidaySelectionNotice, setSiteHolidaySelectionNotice] = useState(false);

    const flatpickrRef = React.useRef(null);
    const displayHolidayDataRef = React.useRef(null);

    const loadDisplayHolidays = React.useCallback(async (year, month) => {
        if (!SITE_ID) return;
        try {
            const response = await API.get(`/sites/display-holidays/?site_id=${SITE_ID}&year=${year}&month=${month}`);
            setDisplayHolidayData(normalizeDisplayHolidayResponse(response.data));
        } catch (error) {
            console.error(error);
            setDisplayHolidayData(normalizeDisplayHolidayResponse());
        }
    }, []);

    const handleCalendarVisibleMonth = React.useCallback((_selectedDates, _dateStr, instance) => {
        if (!instance) return;
        loadDisplayHolidays(instance.currentYear, instance.currentMonth + 1);
    }, [loadDisplayHolidays]);

    const handleDate = (dates) => {
        if (!Array.isArray(dates) || dates.length === 0 || !(dates[0] instanceof Date)) return ["", ""];

        const startDate = moment(dates[0]).format("YYYY-MM-DD");
        if (dates.length < 2 || !(dates[1] instanceof Date)) {
            setSelectedStartDate(startDate);
            setSelectedEndDate("");
            return [startDate, ""];
        }

        const endDate = moment(dates[1]).format("YYYY-MM-DD");
        console.log("startDate", startDate);
        console.log("endDate", endDate);
        setSelectedStartDate(startDate);
        setSelectedEndDate(endDate);

        return [startDate, endDate];
    }

    const abortRef = React.useRef(null);

    const requestPackageProducts = async (dates) => {
        const [startDate, endDate] = handleDate(dates);
        const selectedDateValues = (dates || [])
            .filter((date) => date instanceof Date)
            .map((date) => moment(date).format("YYYY-MM-DD"));
        if (selectedDateValues.some((date) => isSiteDisplayHolidayDate(date, displayHolidayDataRef.current, "lodging"))) {
            setSiteHolidaySelectionNotice(true);
            setPackages([]);
            setToggleId(-1);
            setSelectedSpotId([]);
            return;
        }
        setSiteHolidaySelectionNotice(false);

        try {
            if (abortRef.current) {
                abortRef.current.abort();
            }
            const abortController = new AbortController();
            const signal = abortController.signal;
            abortRef.current = abortController;

            console.log("startDate", startDate);
            console.log("endDate", endDate);

            if (startDate === "" || endDate === "") return;

            const response = await API.get(`${package_api}?start_date=${startDate}&end_date=${endDate}&subdomain=${SUBDOMAIN}${membership_product_id ? `&membership_product_id=${membership_product_id}` : ""}`, {
                signal: signal,
            });
            const data = response.data;

            setPackages(data);
            // setToggleId(-1);
            setSelectedSpotId([]);
        } catch (error) {
            console.error(error);
        }
    };


    // const isWaitState = () => {
    //     let selectedPackage = packages.find((x) => x.id === toggleId);
    //     if (toggleId !== -1 && selectedSpotId.length === 0 && selectedPackage.count <= selectedPackage.reserved) return true;
    //     else return false;
    // };

    useEffect(() => {
        // 컴포넌트가 처음 렌더링될 때 기본 날짜를 설정하고 API 요청을 보냅니다.
        requestPackageProducts([getNextFriday(), getNextSaturday()]);
    }, []);

    useEffect(() => {
        const { year, month } = getDisplayHolidayMonthParams(getNextFriday());
        loadDisplayHolidays(year, month);
    }, [loadDisplayHolidays]);

    useEffect(() => {
        displayHolidayDataRef.current = displayHolidayData;
        flatpickrRef.current?.flatpickr?.redraw?.();
    }, [displayHolidayData]);

    const calendarOptions1 = useMemo(() => ({
        locale: _default.ko,
        inline: true,
        dateFormat: "Y-m-d",
        minDate: "today",
        defaultDate: [getNextFriday(), getNextSaturday()],
        mode: "range",
        disableMobile: true,
        onReady: handleCalendarVisibleMonth,
        onMonthChange: handleCalendarVisibleMonth,
        onYearChange: handleCalendarVisibleMonth,
        onDayCreate: (_dObj, _dateStr, _fp, dayElem) => {
            decorateFlatpickrDay(dayElem, dayElem.dateObj, displayHolidayDataRef.current, "lodging");
        },
    }), [handleCalendarVisibleMonth]);

    const calendarOptions2 = useMemo(() => ({
        locale: _default.ko,
        dateFormat: "Y-m-d",
        minDate: "today",
        defaultDate: [getNextFriday(), getNextSaturday()],
        mode: "range",
        disableMobile: true,
        onReady: handleCalendarVisibleMonth,
        onMonthChange: handleCalendarVisibleMonth,
        onYearChange: handleCalendarVisibleMonth,
        onDayCreate: (_dObj, _dateStr, _fp, dayElem) => {
            decorateFlatpickrDay(dayElem, dayElem.dateObj, displayHolidayDataRef.current, "lodging");
        },
    }), [handleCalendarVisibleMonth]);

    const displayHolidayRows = getDisplayHolidayRows(displayHolidayData, "lodging").slice(0, 8);

    return (
        <Container className="justify-center px-4 md:px-0" style={{ minHeight: "100vh", backgroundColor: "#fff" }}>
            <style>
                {`
        .flatpickr-calendar {
          box-shadow: none !important;
          border: none !important;
          width: 100% !important;
          background: #fff !important;
        }
        .flatpickr-months {
          background: #fff !important;
        }
        .flatpickr-months .flatpickr-month {
          background: #fff !important;
          height: 50px;
          color: #000 !important;
        }
        .flatpickr-innerContainer {
            width: 100% !important;
            border-bottom: none !important;
            border-image: none !important;
            background: unset !important;
        }
        .flatpickr-rContainer {
            width: 100% !important;
        }
        .flatpickr-days {
          width: 100% !important;
          border: none !important;
          display: block !important;
          overflow: visible !important;
        }
        .dayContainer {
          width: 100% !important;
          min-width: 100% !important;
          max-width: 100% !important;
          display: grid !important;
          grid-template-columns: repeat(7, 1fr) !important;
          justify-items: center !important;
          border: none !important;
          border-style: none !important;
          border-image: none !important;
        }
        .dayContainer .flatpickr-day {
          flex: none !important;
          max-width: 40px !important;
          min-width: 40px !important;
          width: 40px !important;
          height: 40px !important;
          box-sizing: border-box !important;
        }
        .flatpickr-day {
          border-radius: 50%;
          margin: 0 auto;
          position: relative !important;
          height: 40px;
          width: 40px;
          min-width: 40px;
          max-width: 40px;
          aspect-ratio: 1;
          line-height: 40px;
          display: flex;
          align-items: center !important;
          justify-content: center !important;
        }
        /* 휴무 표시: 텍스트 배지 대신 숫자 아래 작은 점(dot)으로 시각 노이즈 최소화 */
        .flatpickr-day.site-display-holiday-day::after {
          content: "";
          position: absolute;
          left: 50%;
          bottom: 5px;
          transform: translateX(-50%);
          width: 4px;
          height: 4px;
          border-radius: 999px;
          background: #cbd5e1;
          pointer-events: none;
        }
        .flatpickr-day.site-display-holiday-recurring::after {
          background: #cbd5e1;
        }
        .flatpickr-day.site-display-holiday-site::after {
          background: #e16036;
        }
        .flatpickr-day.selected.site-display-holiday-day::after,
        .flatpickr-day.startRange.site-display-holiday-day::after,
        .flatpickr-day.endRange.site-display-holiday-day::after,
        .flatpickr-day.site-display-holiday-day:hover::after,
        .flatpickr-day.site-display-holiday-day:focus::after {
          background: rgba(255,255,255,0.95);
        }
        .flatpickr-day.inRange {
          background: #E5E5E5 !important;
          border-color: #E5E5E5 !important;
          color: #000 !important;
          box-shadow: -5px 0 0 #E5E5E5, 5px 0 0 #E5E5E5 !important;
        }
        .flatpickr-day.selected, .flatpickr-day.startRange, .flatpickr-day.endRange, .flatpickr-day.selected.inRange, .flatpickr-day.startRange.inRange, .flatpickr-day.endRange.inRange {
          background: #000 !important;
          border-color: #000 !important;
          color: #fff !important;
          box-shadow: none !important;
        }
        /* focus만으로는 채우지 않음 (모바일 탭 후 포커스 잔상이 선택처럼 보이는 문제 방지) */
        .flatpickr-day:focus:not(.selected) {
          background: transparent !important;
          border-color: transparent !important;
          color: inherit !important;
        }
        /* hover는 실제 마우스가 있는 기기에서만 (모바일 sticky hover로 인한 잔상 방지) */
        @media (hover: hover) {
          .flatpickr-day:hover {
            background: #000 !important;
            border-color: #000 !important;
            color: #fff !important;
            box-shadow: none !important;
          }
        }
        .flatpickr-day.today {
        }
        .flatpickr-current-month {
            padding-top: 10px;
            font-size: 110%;
            font-weight: 700;
            color: #000 !important;
            background: transparent !important;
        }
        .flatpickr-current-month .flatpickr-monthDropdown-months {
            color: #000 !important;
            background: transparent !important;
            border: none;
            -webkit-appearance: none;
            appearance: none;
        }
        .flatpickr-current-month input.cur-year {
            color: #000 !important;
            background: transparent !important;
        }
        .flatpickr-next-month, .flatpickr-prev-month {
            padding-top: 10px;
            fill: #000 !important;
            color: #000 !important;
        }
        .flatpickr-next-month svg, .flatpickr-prev-month svg {
            fill: #000 !important;
        }
        .flatpickr-weekday {
            font-weight: 400;
            color: #888;
        }
        .flatpickr-weekdays {
            background: #fff !important;
        }
        span.flatpickr-weekday {
            background: #fff !important;
        }
        `}
            </style>

            {/* Header Section */}
            <div className="flex items-center justify-between py-4 mb-2">
                <h1 className="text-xl font-bold" style={{ fontFamily: "Gmarket-B" }}>숙박예약</h1>
                <div className="flex items-center justify-center px-3 py-1.5 bg-gray-50 rounded-lg text-sm font-medium text-gray-700">
                    <CalendarIcon size={14} className="mr-1.5" />
                    {selectedStartDate && selectedEndDate
                        ? `${moment(selectedStartDate).format("MM.DD")} - ${moment(selectedEndDate).format("MM.DD")}`
                        : selectedStartDate
                            ? moment(selectedStartDate).format("MM.DD")
                            : moment(new Date()).format("YYYY.MM.DD")}
                </div>
            </div>

            <div className="justify-center pb-20">
                {/* Calendar Card */}
                <div className="bg-white rounded-3xl p-4 shadow-[0_2px_20px_rgba(0,0,0,0.04)] mb-4 border border-gray-100">
                    {toggleId === -1 ? (
                        <Flatpickr
                            ref={flatpickrRef}
                            key="1"
                            onChange={requestPackageProducts}
                            className="form-control hidden"
                            options={calendarOptions1}
                        />
                    ) : (
                        <Flatpickr
                            ref={flatpickrRef}
                            key="2"
                            onChange={requestPackageProducts}
                            className="form-control hidden"
                            options={calendarOptions2}
                        />
                    )}
                </div>
                {siteHolidaySelectionNotice && (
                    <div className="mb-4 flex items-start gap-2 rounded-2xl border border-[#f4c8cf] bg-[#fff7f3] px-4 py-3 text-sm font-semibold text-[#9a3412]">
                        <CalendarIcon size={16} className="mt-0.5 shrink-0" />
                        <span>{SITE_DISPLAY_HOLIDAY_CLOSED_MESSAGE}</span>
                    </div>
                )}
                {displayHolidayRows.length > 0 && (
                    <div className="mb-4 flex flex-wrap gap-1.5 px-1">
                        {displayHolidayRows.map((row) => (
                            <span
                                key={row.key || `${row.source}-${row.date || row.weekday}`}
                                className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-gray-700"
                            >
                                {formatDisplayHolidayRowText(row)}
                                <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-500">
                                    {getDisplayHolidaySourceLabel(row)}
                                </span>
                            </span>
                        ))}
                    </div>
                )}

                {/* Available Products Section */}
                <div className="mb-4">
                    <div className="flex items-center gap-2 mb-3">
                        <Clock size={16} className="text-black" />
                        <h3 className="text-lg font-bold" style={{ fontFamily: "Gmarket-B" }}>예약 가능 상품</h3>
                    </div>

                    <div className="flex flex-col gap-3">
                        {packages.length !== 0 ? (
                            packages.map((item) => {
                                return (
                                    <div key={item.id}>
                                        {toggleId === -1 ? (
                                            <div className="bg-gray-50 rounded-[16px] overflow-hidden border border-gray-200">
                                                <div
                                                    className="bg-gray-50 p-4 cursor-pointer transition-colors hover:bg-gray-100/80"
                                                    onClick={() => {
                                                        navigate(`/lodging-package/${item.id}?start-date=${selectedStartDate}&end-date=${selectedEndDate}`);
                                                    }}
                                                >
                                                    <div className="flex flex-row items-center gap-4">
                                                        {/* Image */}
                                                        <div className="flex-shrink-0 w-20 h-20 rounded-[14px] overflow-hidden bg-gray-200">
                                                            {item.images.length !== 0 ? (
                                                                <img src={item.images[0]} className="w-full h-full object-cover" alt={item.name} />
                                                            ) : (
                                                                <div className="w-full h-full flex items-center justify-center">
                                                                    <img src={campIcon} className="w-10 h-10 object-contain opacity-50" alt="default" />
                                                                </div>
                                                            )}
                                                        </div>

                                                        {/* Content */}
                                                        <div className="flex-1 min-w-0 flex flex-col justify-center h-20">
                                                            <h4 className="font-bold text-base mb-1 truncate" style={{ fontFamily: "Gmarket-B" }}>{item.name}</h4>
                                                            <p className="text-xs text-gray-500 mb-1 truncate">{item.simple_desc || item.name}</p>
                                                            <p className="text-xs text-gray-400">
                                                                잔여 {item.proccessed.count - item.proccessed.reserved} / 총 {item.proccessed.count}
                                                            </p>
                                                        </div>

                                                        {/* Price */}
                                                        <div className="flex-shrink-0 flex items-center justify-end">
                                                            {item.proccessed.count <= item.proccessed.reserved || item.proccessed.price === 0 ? (
                                                                <span className="text-gray-400 font-medium">예약마감</span>
                                                            ) : (
                                                                <div className="text-right">
                                                                    <span className="font-bold text-base" style={{ fontFamily: "Gmarket-B" }}>
                                                                        {numberWithCommas(item.proccessed.price)}원
                                                                    </span>
                                                                    <p className="text-xs text-gray-400">(1박) ~</p>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="mb-2">
                                                {item.id === toggleId ? (
                                                    <button
                                                        className="w-full py-2 px-4 bg-black text-white rounded-lg font-bold"
                                                        style={{ fontFamily: "Gmarket-M" }}
                                                        onClick={() => {
                                                            setToggleId(-1);
                                                            setSelectedSpotId([]);
                                                        }}
                                                    >
                                                        목록보기
                                                    </button>
                                                ) : null}
                                            </div>
                                        )}
                                    </div>
                                );
                            })
                        ) : (
                            <div className="flex justify-center py-10">
                                <Oval visible={true} height="40" width="40" color="#000" secondaryColor="#eee" ariaLabel="oval-loading" />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Container>
    );
}

export default LodgingPackageCalendar;
